import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, ZoomControl, Marker, AttributionControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { useDisclosure } from '@chakra-ui/react';
import { debounce } from 'lodash';
import { SearchBar, CenteredSpinner, Logo, TileLayers, OnMapReady } from './components';
import { getAllHoops } from './services/firestore';
import { adjustViewportHeight, getMarkerIcon } from './utils';
import { enableAttribution, map } from './config';
import HoopsDrawer from './features/HoopsDrawer';
import UploadDrawer from './features/UploadDrawer';

const App = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hoops, setHoops] = useState([]);
  const [activeHoop, setActiveHoop] = useState(null);
  const {
    isOpen: isHoopsDrawerOpen,
    onOpen: onOpenHoopsDrawer,
    onClose: onCloseHoopsDrawer,
  } = useDisclosure();
  const {
    isOpen: isUploadDrawerOpen,
    onOpen: onOpenUploadDrawer,
    onClose: onCloseUploadDrawer,
  } = useDisclosure();
  const markerClusterRef = useRef();

  useEffect(() => {
    const fetchHoops = async () => {
      try {
        setIsLoading(true);
        const querySnapshot = await getAllHoops();

        const data = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        setHoops(data);
      } catch (error) {
        console.error('Error fetching hoops', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHoops();
  }, []);

  useEffect(() => {
    const adjustVhListener = debounce(adjustViewportHeight, 200);

    window.addEventListener('resize', adjustVhListener);

    return () => {
      window.removeEventListener('resize', adjustVhListener);
    };
  }, []);

  const handleDrawerClose = useCallback(() => {
    onCloseHoopsDrawer();
  }, [onCloseHoopsDrawer]);

  const onClickMarker = useCallback(
    ({ target }) => {
      setActiveHoop({ ...target.options, position: target.getLatLng() });
      onOpenHoopsDrawer();
    },
    [onOpenHoopsDrawer],
  );

  const onClusterClick = useCallback(({ layer }) => {
    const { _zoom: currentZoom } = layer;

    if (currentZoom < map.maxZoomLevel) {
      layer.unspiderfy();
      layer.zoomToBounds({ maxZoom: map.maxZoomLevel });
    } else {
      layer.spiderfy();
    }
  }, []);

  const renderHoopMarker = ({ id, name, location }) => {
    const markerIcon = getMarkerIcon({ name });
    const position = { lat: location.coordinates.latitude, lng: location.coordinates.longitude };

    return (
      <Marker
        key={id}
        id={id}
        name={name}
        alt={name}
        location={location}
        icon={markerIcon}
        position={position}
        eventHandlers={{ click: onClickMarker }}
      />
    );
  };

  return (
    <MapContainer
      id="map-container"
      center={map.defaultCenter}
      zoom={map.defaultZoomLevel}
      minZoom={map.minZoomLevel}
      maxBounds={map.maxBoundsWorld}
      maxBoundsViscosity={1.0}
      worldCopyJump
      zoomControl={false}
      boxZoom={false}
      bounceAtZoomLimits={false}
      attributionControl={false}
      tap={false}
    >
      <OnMapReady />

      <TileLayers />

      <MarkerClusterGroup
        ref={markerClusterRef}
        showCoverageOnHover={false}
        zoomToBoundsOnClick={false}
        spiderfyOnMaxZoom={false}
        disableClusteringAtZoom={13}
        maxClusterRadius={80}
        animate={false}
        chunkedLoading
        removeOutsideVisibleBounds
        spiderLegPolylineOptions={{ weight: 1 }}
        onClick={onClusterClick}
      >
        {hoops.length > 0 && hoops.map(renderHoopMarker)}
      </MarkerClusterGroup>

      {isHoopsDrawerOpen && !isUploadDrawerOpen && (
        <HoopsDrawer
          isOpen={isHoopsDrawerOpen}
          activeHoop={activeHoop}
          onClose={handleDrawerClose}
          openUploadModal={onOpenUploadDrawer}
        />
      )}

      {isUploadDrawerOpen && activeHoop?.id && (
        <UploadDrawer
          isOpen={isUploadDrawerOpen}
          onClose={onCloseUploadDrawer}
          hoopId={activeHoop.id}
          hoopName={activeHoop.name}
        />
      )}

      {!isHoopsDrawerOpen && !isUploadDrawerOpen && (
        <>
          <SearchBar />
          <ZoomControl position="bottomright" />
        </>
      )}

      <Logo />
      {isLoading && <CenteredSpinner color="knicks.500" />}
      {enableAttribution && <AttributionControl position="bottomleft" />}
    </MapContainer>
  );
};

export default App;
